/* eslint-disable react/prop-types */
// prop-types disabled: the modals/rows here are internal to this file only.
import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Tab,
  Table,
  Tabs
} from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import IconButton from 'components/common/IconButton';
import LoadingButton from 'components/common/LoadingButton';
import LoadingContainer from 'components/common/LoadingContainer';
import SoftBadge from 'components/common/SoftBadge';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import AddCardModal from 'components/common/AddCardModal';
import { formatMoney } from 'helpers/utils';
import paths from 'routes/paths';
import {
  usePledge,
  useUpdatePledge,
  useCreatePledgeFollowUp,
  useCompletePledgeFollowUp
} from 'hooks/usePledges';
import {
  useCreateOrder,
  useDeleteOrder,
  useGetPaymentMethods,
  useProcessPledgePayment
} from 'hooks/useStripePayments';
import { useQueryClient } from '@tanstack/react-query';
import {
  PLEDGE_STATUSES,
  PLEDGE_FREQUENCIES,
  PLEDGE_SOURCES,
  PLEDGE_PRIORITIES,
  CONTACT_METHODS,
  pledgeRef,
  donorFullName
} from './pledgeConstants';

const OFFLINE_PAYMENT_TYPES = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'direct_debit', label: 'Direct Debit' }
];

// Record an offline payment against the pledge. Saves a normal donation
// (order) tagged with the pledge id, so donor history/receipts/gift aid all
// behave exactly like any other donation.
const RecordPaymentModal = ({ show, onHide, pledge, onRecorded }) => {
  const { createOrderAsync, isCreating } = useCreateOrder();
  const [amount, setAmount] = useState(pledge?.amount || '');
  const [paymentType, setPaymentType] = useState('cash');
  const [paymentDate, setPaymentDate] = useState(dayjs().format('YYYY-MM-DD'));

  const handleSubmit = async () => {
    try {
      const orderDetails = [
        {
          amount: Number(amount),
          billing_condition: 'one-off',
          donation_type_id: null,
          package_id: pledge.package_id || null
        }
      ];
      const response = await createOrderAsync({
        donorId: pledge.donar_id,
        branchId: pledge.branch_id,
        orderDetails,
        paymentType,
        subtotal: Number(amount),
        transactionFees: 0,
        donationAmount: Number(amount),
        giftAidEligible: false,
        paymentDate: paymentDate || undefined,
        pledgeId: pledge.id
      });
      if (response?.message === 'success') {
        onRecorded();
        onHide();
      }
    } catch (e) {
      // toast handled by hook
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Record Payment — {pledgeRef(pledge)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          <Col xs={12}>
            <div className="bg-light rounded p-2 fs--1">
              Outstanding:{' '}
              <strong>
                {pledge.outstanding === null
                  ? 'open-ended'
                  : formatMoney(pledge.outstanding)}
              </strong>
            </div>
          </Col>
          <Col md={6}>
            <Form.Label>Amount *</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={e => setAmount(e.target.value)}
            />
          </Col>
          <Col md={6}>
            <Form.Label>Payment method</Form.Label>
            <Form.Select
              value={paymentType}
              onChange={e => setPaymentType(e.target.value)}
            >
              {OFFLINE_PAYMENT_TYPES.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={6}>
            <Form.Label>Payment date</Form.Label>
            <Form.Control
              type="date"
              value={paymentDate}
              onChange={e => setPaymentDate(e.target.value)}
            />
          </Col>
        </Row>
        <p className="text-500 fs--1 mt-3 mb-0">
          Card payments can be taken through Add Donation, or automatically on
          schedule for pledges set up with a saved card.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={onHide}>
          Cancel
        </Button>
        <LoadingButton
          loading={isCreating}
          disabled={!(Number(amount) > 0) || isCreating}
          onClick={handleSubmit}
        >
          Record Payment
        </LoadingButton>
      </Modal.Footer>
    </Modal>
  );
};

// Change how the pledge is paid: switch between staff follow-up and
// automatic card payments, pick a different saved card, move the next
// payment date, or toggle Gift Aid on future automatic payments.
const PaymentSettingsModal = ({ show, onHide, pledge, onSaved }) => {
  const { updatePledge, isUpdating } = useUpdatePledge();
  const { getPaymentMethods } = useGetPaymentMethods();

  const [preference, setPreference] = useState(
    pledge?.payment_preference === 'auto' ? 'auto' : 'contact'
  );
  const [savedCards, setSavedCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardId, setCardId] = useState(pledge?.stripe_payment_method_id || '');
  const [nextDate, setNextDate] = useState(
    pledge?.next_due_date || dayjs().format('YYYY-MM-DD')
  );
  const [giftAid, setGiftAid] = useState(Boolean(pledge?.gift_aid_eligible));
  const [showAddCard, setShowAddCard] = useState(false);

  const handleCardAdded = paymentMethod => {
    setSavedCards(prev => [...prev, paymentMethod]);
    setCardId(paymentMethod.id);
  };

  useEffect(() => {
    if (!show || preference !== 'auto' || !pledge?.donar_id) return;

    let cancelled = false;
    setCardsLoading(true);
    getPaymentMethods(pledge.donar_id)
      .then(result => {
        if (cancelled) return;
        const cards = result?.payment_methods || [];
        setSavedCards(cards);
        setCardId(
          current =>
            current || result?.default_payment_method_id || cards[0]?.id || ''
        );
      })
      .catch(() => {
        if (!cancelled) setSavedCards([]);
      })
      .finally(() => {
        if (!cancelled) setCardsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, preference, pledge?.donar_id]);

  const isAuto = preference === 'auto';
  const canSave = !isAuto || (cardId && nextDate);

  const handleSave = () => {
    const data = isAuto
      ? {
          payment_preference: 'auto',
          stripe_payment_method_id: cardId,
          next_due_date: nextDate,
          start_date: pledge.start_date || nextDate,
          gift_aid_eligible: giftAid,
          // Workflow statuses hand over to the schedule; money statuses
          // (active/partially_paid) stay as they are
          ...(['new', 'awaiting_follow_up'].includes(pledge.status)
            ? { status: 'payment_scheduled' }
            : {})
        }
      : {
          payment_preference: 'contact',
          stripe_payment_method_id: null,
          next_due_date: null,
          ...(pledge.status === 'payment_scheduled'
            ? { status: 'awaiting_follow_up' }
            : {})
        };

    updatePledge(
      { pledgeId: pledge.id, data },
      {
        onSuccess: () => {
          onSaved();
          onHide();
        }
      }
    );
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      {showAddCard && (
        <AddCardModal
          show={showAddCard}
          onHide={() => setShowAddCard(false)}
          donorId={pledge.donar_id}
          onAdded={handleCardAdded}
        />
      )}
      <Modal.Header closeButton>
        <Modal.Title>Payment Settings — {pledgeRef(pledge)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <Form.Check
            type="radio"
            id="settings-pref-contact"
            name="settings-payment-preference"
            label="Staff follow up — payments recorded manually"
            checked={preference === 'contact'}
            onChange={() => setPreference('contact')}
          />
          <Form.Check
            type="radio"
            id="settings-pref-auto"
            name="settings-payment-preference"
            label="Automatic card payment on each due date"
            checked={preference === 'auto'}
            onChange={() => setPreference('auto')}
          />
        </div>
        {isAuto && (
          <Row className="g-3">
            <Col md={6}>
              <Form.Label>Next payment date *</Form.Label>
              <Form.Control
                type="date"
                value={nextDate}
                onChange={e => setNextDate(e.target.value)}
              />
            </Col>
            <Col md={12}>
              <Form.Label>Saved card *</Form.Label>
              {cardsLoading ? (
                <p className="text-600 fs--1 mb-0 mt-1">Loading cards…</p>
              ) : savedCards.length === 0 ? (
                <div className="mt-1">
                  <p className="text-warning fs--1 mb-2">
                    This donor has no saved card yet.
                  </p>
                  <Button
                    variant="falcon-primary"
                    size="sm"
                    onClick={() => setShowAddCard(true)}
                  >
                    Add a card
                  </Button>
                </div>
              ) : (
                <>
                  <Form.Select
                    value={cardId}
                    onChange={e => setCardId(e.target.value)}
                  >
                    {savedCards.map(pm => (
                      <option key={pm.id} value={pm.id}>
                        {(pm.card?.brand || 'card').toUpperCase()} ••••{' '}
                        {pm.card?.last4} — expires {pm.card?.exp_month}/
                        {pm.card?.exp_year}
                      </option>
                    ))}
                  </Form.Select>
                  <Button
                    variant="link"
                    size="sm"
                    className="px-0 fs--1"
                    onClick={() => setShowAddCard(true)}
                  >
                    + Add a different card
                  </Button>
                </>
              )}
            </Col>
            <Col md={12}>
              <Form.Check
                type="checkbox"
                id="settings-gift-aid"
                label="Claim Gift Aid on these payments (donor must have a valid declaration)"
                checked={giftAid}
                onChange={e => setGiftAid(e.target.checked)}
              />
            </Col>
          </Row>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={onHide}>
          Cancel
        </Button>
        <LoadingButton
          loading={isUpdating}
          disabled={!canSave || isUpdating}
          onClick={handleSave}
        >
          Save Settings
        </LoadingButton>
      </Modal.Footer>
    </Modal>
  );
};

const FollowUpModal = ({ show, onHide, pledge, onSaved }) => {
  const { createFollowUpAsync, isCreating } = useCreatePledgeFollowUp();
  const { updatePledgeAsync } = useUpdatePledge();
  const [dueDate, setDueDate] = useState(
    dayjs().add(7, 'day').format('YYYY-MM-DD')
  );
  const [method, setMethod] = useState('phone');
  const [notes, setNotes] = useState('');

  const handleSubmit = async () => {
    try {
      await createFollowUpAsync({
        pledge_id: pledge.id,
        due_date: dueDate,
        method,
        notes: notes || null
      });
      await updatePledgeAsync({
        pledgeId: pledge.id,
        data: { follow_up_date: dueDate, status: 'awaiting_follow_up' }
      });
      onSaved();
      onHide();
    } catch (e) {
      // toasts handled by hooks
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Schedule Follow-Up</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          <Col md={6}>
            <Form.Label>Due date *</Form.Label>
            <Form.Control
              type="date"
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
            />
          </Col>
          <Col md={6}>
            <Form.Label>Method</Form.Label>
            <Form.Select
              value={method}
              onChange={e => setMethod(e.target.value)}
            >
              {Object.entries(CONTACT_METHODS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={12}>
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={onHide}>
          Cancel
        </Button>
        <LoadingButton
          loading={isCreating}
          disabled={!dueDate || isCreating}
          onClick={handleSubmit}
        >
          Save
        </LoadingButton>
      </Modal.Footer>
    </Modal>
  );
};

const CompleteFollowUpModal = ({ show, onHide, followUp, onSaved }) => {
  const { completeFollowUpAsync, isCompleting } = useCompletePledgeFollowUp();
  const [outcome, setOutcome] = useState('');

  const handleSubmit = async () => {
    try {
      await completeFollowUpAsync({ followUpId: followUp.id, outcome });
      onSaved();
      onHide();
    } catch (e) {
      // toast handled by hook
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Record Follow-Up Outcome</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Label>What happened? *</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={outcome}
          onChange={e => setOutcome(e.target.value)}
          placeholder="e.g. Spoke to donor — will pay by bank transfer next week"
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={onHide}>
          Cancel
        </Button>
        <LoadingButton
          loading={isCompleting}
          disabled={!outcome.trim() || isCompleting}
          onClick={handleSubmit}
        >
          Save Outcome
        </LoadingButton>
      </Modal.Footer>
    </Modal>
  );
};

const StatRow = ({ label, value }) => (
  <tr>
    <td className="fw-medium text-600" style={{ width: '40%' }}>
      {label}
    </td>
    <td className="fw-medium text-900">{value}</td>
  </tr>
);

// Edit the pledge itself: the amount (and installments for fixed-term
// pledges), where it came from and the event it was made at. Money already
// received is untouched - totals are re-derived from the new amount.
const EditPledgeModal = ({ show, onHide, pledge, onSaved }) => {
  const { updatePledge, isUpdating } = useUpdatePledge();
  const [amount, setAmount] = useState(pledge?.amount ?? '');
  const [installments, setInstallments] = useState(pledge?.installments ?? '');
  const [source, setSource] = useState(pledge?.source || '');
  const [eventName, setEventName] = useState(pledge?.event_name || '');
  const [eventLocation, setEventLocation] = useState(
    pledge?.event_location || ''
  );
  const [eventDate, setEventDate] = useState(pledge?.event_date || '');
  const [priority, setPriority] = useState(pledge?.priority || 'normal');
  const [notes, setNotes] = useState(pledge?.notes || '');

  const isFixed =
    pledge?.duration_type === 'fixed' && pledge?.frequency !== 'one_off';
  const numAmount = Number(amount) || 0;
  const numInstallments = Number(installments) || 0;
  const totalValue =
    pledge?.frequency === 'one_off'
      ? numAmount
      : isFixed && numInstallments > 0
      ? numAmount * numInstallments
      : null;

  const canSave = numAmount > 0 && (!isFixed || numInstallments > 0);

  const handleSave = () => {
    updatePledge(
      {
        pledgeId: pledge.id,
        data: {
          amount: numAmount,
          installments: isFixed ? numInstallments : pledge.installments,
          total_value: totalValue,
          source: source || null,
          event_name: source === 'event' ? eventName || null : null,
          event_location: source === 'event' ? eventLocation || null : null,
          event_date: source === 'event' ? eventDate || null : null,
          priority,
          notes: notes || null
        }
      },
      {
        onSuccess: () => {
          onSaved?.();
          onHide();
        }
      }
    );
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title as="h5">Edit Pledge — {pledgeRef(pledge)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          <Col md={4}>
            <Form.Label>
              Amount {pledge?.frequency !== 'one_off' && 'per payment'} *
            </Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={e => setAmount(e.target.value)}
            />
          </Col>
          {isFixed && (
            <Col md={4}>
              <Form.Label>Number of payments *</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={installments}
                onChange={e => setInstallments(e.target.value)}
              />
            </Col>
          )}
          <Col md={4}>
            <Form.Label>Total pledged</Form.Label>
            <div className="form-control bg-light">
              {totalValue !== null
                ? formatMoney(totalValue)
                : `${formatMoney(numAmount)} per ${
                    pledge?.frequency === 'annually' ? 'year' : 'month'
                  }, open-ended`}
            </div>
          </Col>
          <Col md={4}>
            <Form.Label>Priority</Form.Label>
            <Form.Select
              value={priority}
              onChange={e => setPriority(e.target.value)}
            >
              {Object.entries(PLEDGE_PRIORITIES).map(([key, { label }]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={4}>
            <Form.Label>Source</Form.Label>
            <Form.Select
              value={source}
              onChange={e => setSource(e.target.value)}
            >
              <option value="">Not recorded</option>
              {Object.entries(PLEDGE_SOURCES).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </Form.Select>
          </Col>
          {source === 'event' && (
            <>
              <Col md={4}>
                <Form.Label>Event title</Form.Label>
                <Form.Control
                  value={eventName}
                  onChange={e => setEventName(e.target.value)}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Event location</Form.Label>
                <Form.Control
                  value={eventLocation}
                  onChange={e => setEventLocation(e.target.value)}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Event date</Form.Label>
                <Form.Control
                  type="date"
                  value={eventDate}
                  onChange={e => setEventDate(e.target.value)}
                />
              </Col>
            </>
          )}
          <Col md={12}>
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </Col>
        </Row>
        {pledge?.payment_preference === 'auto' && (
          <p className="text-600 fs--1 mt-3 mb-0">
            This pledge is charged automatically: the new amount applies from
            the next scheduled payment.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={onHide}>
          Cancel
        </Button>
        <LoadingButton
          loading={isUpdating}
          disabled={!canSave || isUpdating}
          onClick={handleSave}
        >
          Save Changes
        </LoadingButton>
      </Modal.Footer>
    </Modal>
  );
};

const PledgeProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { pledge, isLoading, refetch } = usePledge(id);
  const { updatePledge, isUpdating } = useUpdatePledge();
  const { processPledgePaymentAsync, isProcessingPledge } =
    useProcessPledgePayment();

  const [showPayment, setShowPayment] = useState(false);
  const [showPaymentSettings, setShowPaymentSettings] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const { deleteOrderAsync, isDeleting: isDeletingPayment } = useDeleteOrder();
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const handleDeletePayment = async () => {
    try {
      await deleteOrderAsync({ orderId: paymentToDelete.id });
      setPaymentToDelete(null);
    } catch (e) {
      // toast handled by hook
    }
  };

  const [showFollowUp, setShowFollowUp] = useState(false);
  const [completingFollowUp, setCompletingFollowUp] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // 'hold' | 'resume' | 'cancel'

  if (isLoading) return <LoadingContainer />;
  if (!pledge) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <h5 className="text-600">Pledge not found</h5>
          <Button variant="link" onClick={() => navigate(paths.pledges)}>
            Back to pledges
          </Button>
        </Card.Body>
      </Card>
    );
  }

  const status = PLEDGE_STATUSES[pledge.effectiveStatus] || {
    label: pledge.effectiveStatus,
    bg: 'secondary'
  };
  const payments = [...(pledge.orders || [])].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );
  const followUps = [...(pledge.follow_ups || [])].sort(
    (a, b) => new Date(b.due_date) - new Date(a.due_date)
  );
  const nextFollowUp = followUps.find(f => f.status === 'pending');

  const isAutoPledge =
    pledge.payment_preference === 'auto' && pledge.stripe_payment_method_id;

  const nextAction =
    pledge.effectiveStatus === 'cancelled'
      ? 'None — pledge cancelled'
      : pledge.effectiveStatus === 'fulfilled'
      ? 'None — pledge fulfilled 🎉'
      : pledge.effectiveStatus === 'on_hold'
      ? 'On hold — resume when ready'
      : isAutoPledge && pledge.next_due_date
      ? `Automatic card payment of ${formatMoney(
          Number(pledge.amount)
        )} on ${dayjs(pledge.next_due_date).format('DD MMM YYYY')}`
      : nextFollowUp
      ? `Follow up on ${dayjs(nextFollowUp.due_date).format('DD MMM YYYY')} (${
          CONTACT_METHODS[nextFollowUp.method] || nextFollowUp.method || '—'
        })`
      : 'Record a payment or schedule a follow-up';

  const handleConfirmAction = () => {
    const updates =
      confirmAction === 'cancel'
        ? {
            status: 'cancelled',
            cancelled_at: new Date().toISOString()
          }
        : confirmAction === 'hold'
        ? { status: 'on_hold' }
        : { status: 'awaiting_follow_up' };

    updatePledge(
      { pledgeId: pledge.id, data: updates },
      {
        onSuccess: () => {
          setConfirmAction(null);
          refetch();
        }
      }
    );
  };

  const refreshAll = () => {
    refetch();
    queryClient.invalidateQueries({ queryKey: ['pledges'] });
  };

  const handleChargeNow = async () => {
    if (
      !window.confirm(
        `Charge ${formatMoney(
          Number(pledge.amount)
        )} to the donor's saved card now?`
      )
    ) {
      return;
    }
    try {
      await processPledgePaymentAsync({ pledgeId: pledge.id });
    } catch (e) {
      // Error toast handled by the hook
    }
    refreshAll();
  };

  return (
    <>
      {showPayment && (
        <RecordPaymentModal
          show={showPayment}
          onHide={() => setShowPayment(false)}
          pledge={pledge}
          onRecorded={refreshAll}
        />
      )}
      {showEdit && (
        <EditPledgeModal
          show={showEdit}
          onHide={() => setShowEdit(false)}
          pledge={pledge}
          onSaved={refreshAll}
        />
      )}
      {showPaymentSettings && (
        <PaymentSettingsModal
          show={showPaymentSettings}
          onHide={() => setShowPaymentSettings(false)}
          pledge={pledge}
          onSaved={refreshAll}
        />
      )}
      {showFollowUp && (
        <FollowUpModal
          show={showFollowUp}
          onHide={() => setShowFollowUp(false)}
          pledge={pledge}
          onSaved={refreshAll}
        />
      )}
      {completingFollowUp && (
        <CompleteFollowUpModal
          show={!!completingFollowUp}
          onHide={() => setCompletingFollowUp(null)}
          followUp={completingFollowUp}
          onSaved={refreshAll}
        />
      )}
      <ConfirmationModal
        show={!!paymentToDelete}
        onHide={() => setPaymentToDelete(null)}
        loading={isDeletingPayment}
        title="Remove payment"
        confirmText="Remove payment"
        handleConfirm={handleDeletePayment}
        body={
          paymentToDelete
            ? `Remove the ${formatMoney(
                Number(paymentToDelete.donation_amount)
              )} payment #${
                paymentToDelete.simple_id
              }? This deletes the donation record everywhere, including the donor's Giving tab.`
            : ''
        }
      />
      <ConfirmationModal
        show={!!confirmAction}
        onHide={() => setConfirmAction(null)}
        loading={isUpdating}
        title={
          confirmAction === 'cancel'
            ? 'Cancel Pledge'
            : confirmAction === 'hold'
            ? 'Put Pledge On Hold'
            : 'Resume Pledge'
        }
        handleConfirm={handleConfirmAction}
        body={
          confirmAction === 'cancel'
            ? 'Are you sure you want to cancel this pledge? Payments already recorded are kept.'
            : confirmAction === 'hold'
            ? 'Put this pledge on hold? Follow-ups will pause until resumed.'
            : 'Resume this pledge?'
        }
      />

      {/* Header */}
      <Card className="mb-3">
        <Card.Body>
          <Flex
            justifyContent="between"
            alignItems="start"
            className="flex-column flex-md-row gap-2"
          >
            <div>
              <Flex alignItems="center" className="mb-1 flex-wrap gap-2">
                <h4 className="mb-0">
                  <Link to={`/donors/${pledge.donor?.id}`}>
                    {donorFullName(pledge.donor)}
                  </Link>
                </h4>
                <span className="text-500">{pledgeRef(pledge)}</span>
                <SoftBadge pill bg={status.bg}>
                  {status.label}
                </SoftBadge>
                {pledge.priority !== 'normal' && (
                  <SoftBadge
                    pill
                    bg={PLEDGE_PRIORITIES[pledge.priority]?.bg || 'secondary'}
                  >
                    {PLEDGE_PRIORITIES[pledge.priority]?.label}
                  </SoftBadge>
                )}
              </Flex>
              <p className="mb-0 text-600 fs--1">
                {pledge.package?.package_title || 'No campaign'} ·{' '}
                {PLEDGE_FREQUENCIES[pledge.frequency]}
                {pledge.source && pledge.source !== 'other'
                  ? ` · ${PLEDGE_SOURCES[pledge.source] || pledge.source}`
                  : ''}
                {pledge.event_name ? ` (${pledge.event_name})` : ''}
              </p>
            </div>
            <Flex className="gap-2 flex-wrap">
              {!['cancelled', 'fulfilled'].includes(pledge.effectiveStatus) && (
                <>
                  {isAutoPledge && (
                    <LoadingButton
                      loading={isProcessingPledge}
                      variant="success"
                      size="sm"
                      onClick={handleChargeNow}
                    >
                      Charge Card Now
                    </LoadingButton>
                  )}
                  <IconButton
                    icon="plus"
                    variant="primary"
                    size="sm"
                    onClick={() => setShowPayment(true)}
                  >
                    Record Payment
                  </IconButton>
                  <IconButton
                    icon="calendar-alt"
                    variant="falcon-default"
                    size="sm"
                    onClick={() => setShowFollowUp(true)}
                  >
                    Schedule Follow-Up
                  </IconButton>
                  <IconButton
                    icon="credit-card"
                    variant="falcon-default"
                    size="sm"
                    onClick={() => setShowPaymentSettings(true)}
                  >
                    Payment Settings
                  </IconButton>
                  <IconButton
                    icon="edit"
                    variant="falcon-default"
                    size="sm"
                    onClick={() => setShowEdit(true)}
                  >
                    Edit Pledge
                  </IconButton>
                  {pledge.effectiveStatus === 'on_hold' ? (
                    <Button
                      variant="falcon-success"
                      size="sm"
                      onClick={() => setConfirmAction('resume')}
                    >
                      Resume
                    </Button>
                  ) : (
                    <Button
                      variant="falcon-default"
                      size="sm"
                      onClick={() => setConfirmAction('hold')}
                    >
                      On Hold
                    </Button>
                  )}
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setConfirmAction('cancel')}
                  >
                    Cancel Pledge
                  </Button>
                </>
              )}
            </Flex>
          </Flex>

          <Row className="g-3 mt-1">
            <Col xs={6} md={3}>
              <div className="border rounded p-2 text-center">
                <p className="text-600 fs--2 mb-1">
                  {pledge.total_value ? 'Total Pledged' : 'Pledge Amount'}
                </p>
                <h5 className="mb-0">
                  {pledge.total_value
                    ? formatMoney(Number(pledge.total_value))
                    : formatMoney(Number(pledge.amount))}
                </h5>
                {!pledge.total_value && (
                  // Open-ended pledges have no total: say so instead of a bare "/mo"
                  <p className="text-500 fs--2 mb-0">
                    per {pledge.frequency === 'annually' ? 'year' : 'month'},
                    until cancelled
                  </p>
                )}
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="border rounded p-2 text-center">
                <p className="text-600 fs--2 mb-1">Paid So Far</p>
                <h5 className="mb-0 text-success">
                  {formatMoney(pledge.paidTotal)}
                </h5>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="border rounded p-2 text-center">
                <p className="text-600 fs--2 mb-1">Outstanding</p>
                <h5 className="mb-0 text-warning">
                  {pledge.outstanding === null
                    ? '—'
                    : formatMoney(pledge.outstanding)}
                </h5>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="border rounded p-2 text-center">
                <p className="text-600 fs--2 mb-1">Assigned</p>
                <h5 className="mb-0">{donorFullName(pledge.assigned_user)}</h5>
              </div>
            </Col>
          </Row>
          {(pledge.source === 'event' || pledge.event_name) && (
            <div className="border rounded p-3 mt-3 bg-light">
              <Flex
                justifyContent="between"
                alignItems="center"
                className="flex-wrap gap-2"
              >
                <div>
                  <p className="text-600 fs--2 mb-1">
                    <FontAwesomeIcon icon="calendar-alt" className="me-1" />
                    Event
                  </p>
                  <h6 className="mb-0">
                    {pledge.event_name || 'Untitled event'}
                  </h6>
                  <p className="text-600 fs--1 mb-0">
                    {[
                      pledge.event_location,
                      pledge.event_date
                        ? dayjs(pledge.event_date).format('DD MMM YYYY')
                        : null
                    ]
                      .filter(Boolean)
                      .join(' · ') || 'No location or date recorded'}
                  </p>
                </div>
                <div className="text-end">
                  <p className="text-600 fs--2 mb-1">Received so far</p>
                  <h5 className="mb-0 text-success">
                    {formatMoney(pledge.paidTotal)}
                    {pledge.total_value ? (
                      <span className="text-500 fs--1">
                        {' '}
                        / {formatMoney(Number(pledge.total_value))}
                      </span>
                    ) : null}
                  </h5>
                </div>
              </Flex>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Tabs */}
      <Card>
        <Card.Body>
          <Tabs defaultActiveKey="overview" className="mb-3">
            <Tab eventKey="overview" title="Overview">
              <div className="bg-light rounded p-3 mb-3">
                <span className="fw-medium text-600 me-2">Next action:</span>
                <span className="fw-medium text-900">{nextAction}</span>
              </div>
              <Table size="sm" className="mb-0">
                <tbody>
                  <StatRow
                    label="Amount per payment"
                    value={formatMoney(Number(pledge.amount))}
                  />
                  <StatRow
                    label="Frequency"
                    value={PLEDGE_FREQUENCIES[pledge.frequency]}
                  />
                  <StatRow
                    label="Duration"
                    value={
                      pledge.duration_type === 'fixed'
                        ? `${pledge.installments} payment${
                            pledge.installments > 1 ? 's' : ''
                          }`
                        : 'Until cancelled'
                    }
                  />
                  <StatRow
                    label="Preferred contact"
                    value={
                      CONTACT_METHODS[pledge.preferred_contact_method] || '—'
                    }
                  />
                  <StatRow
                    label="Created"
                    value={dayjs(pledge.created_at).format('DD MMM YYYY')}
                  />
                  {pledge.cancelled_at && (
                    <StatRow
                      label="Cancelled"
                      value={dayjs(pledge.cancelled_at).format('DD MMM YYYY')}
                    />
                  )}
                </tbody>
              </Table>
            </Tab>

            <Tab eventKey="payments" title={`Payments (${payments.length})`}>
              {payments.length === 0 ? (
                <p className="text-600 text-center py-4 mb-0">
                  No payments recorded yet
                </p>
              ) : (
                <Table responsive size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Ref</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map(order => (
                      <tr key={order.id}>
                        <td>
                          <Link
                            to={`/donors/${pledge.donor?.id}/giving/${order.id}`}
                          >
                            #{order.simple_id}
                          </Link>
                        </td>
                        <td>{dayjs(order.created_at).format('DD/MM/YYYY')}</td>
                        <td>{formatMoney(Number(order.donation_amount))}</td>
                        <td>
                          <SoftBadge
                            pill
                            bg={
                              order.payment_status === 'paid'
                                ? 'success'
                                : 'warning'
                            }
                            className="text-capitalize"
                          >
                            {order.payment_status}
                          </SoftBadge>
                        </td>
                        <td className="text-end">
                          <Button
                            variant="link"
                            size="sm"
                            className="text-danger p-0"
                            title="Remove this payment (deletes the donation record too)"
                            onClick={() => setPaymentToDelete(order)}
                          >
                            Remove
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Tab>

            <Tab
              eventKey="followups"
              title={`Follow-Ups (${followUps.length})`}
            >
              {followUps.length === 0 ? (
                <p className="text-600 text-center py-4 mb-0">
                  No follow-ups scheduled
                </p>
              ) : (
                <Table responsive size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Due</th>
                      <th>Method</th>
                      <th>Notes</th>
                      <th>Outcome</th>
                      <th>Added by</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {followUps.map(fu => (
                      <tr key={fu.id}>
                        <td>{dayjs(fu.due_date).format('DD/MM/YYYY')}</td>
                        <td>
                          {CONTACT_METHODS[fu.method] || fu.method || '—'}
                        </td>
                        <td className="text-600">{fu.notes || '—'}</td>
                        <td className="text-600">{fu.outcome || '—'}</td>
                        <td className="text-600 fs--1">
                          {donorFullName(fu.created_by_user) || '—'}
                          {fu.status === 'completed' &&
                            fu.completed_by_user && (
                              <div className="text-500">
                                Completed by{' '}
                                {donorFullName(fu.completed_by_user)}
                              </div>
                            )}
                        </td>
                        <td>
                          <SoftBadge
                            pill
                            bg={
                              fu.status === 'completed'
                                ? 'success'
                                : dayjs(fu.due_date).isBefore(dayjs(), 'day')
                                ? 'danger'
                                : 'warning'
                            }
                            className="text-capitalize"
                          >
                            {fu.status === 'pending' &&
                            dayjs(fu.due_date).isBefore(dayjs(), 'day')
                              ? 'Overdue'
                              : fu.status}
                          </SoftBadge>
                        </td>
                        <td className="text-end">
                          {fu.status === 'pending' && (
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0"
                              onClick={() => setCompletingFollowUp(fu)}
                            >
                              <FontAwesomeIcon icon="check" className="me-1" />
                              Record outcome
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Tab>

            <Tab eventKey="notes" title="Notes">
              <p className="text-700 mb-1" style={{ whiteSpace: 'pre-wrap' }}>
                {pledge.notes || 'No notes'}
              </p>
              {pledge.notes && (
                <p className="text-500 fs--1 mb-0">
                  Added by {donorFullName(pledge.created_by_user) || 'unknown'}{' '}
                  on {dayjs(pledge.created_at).format('DD/MM/YYYY')}
                  {pledge.updated_at &&
                    pledge.updated_at !== pledge.created_at &&
                    ` · last updated ${dayjs(pledge.updated_at).format(
                      'DD/MM/YYYY'
                    )}`}
                </p>
              )}
            </Tab>

            <Tab
              eventKey="history"
              title={`History (${(pledge.changes || []).length})`}
            >
              {/* #29: every change to amount / instalments / frequency / total */}
              {(pledge.changes || []).length === 0 ? (
                <p className="text-600 text-center py-4 mb-0">
                  No changes recorded yet
                </p>
              ) : (
                <Table responsive size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Field</th>
                      <th>From</th>
                      <th>To</th>
                      <th>Changed by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...pledge.changes]
                      .sort(
                        (a, b) =>
                          new Date(b.changed_at) - new Date(a.changed_at)
                      )
                      .map(ch => {
                        const money = ['amount', 'total_value'].includes(
                          ch.field
                        );
                        const fmt = v =>
                          v === null || v === undefined || v === ''
                            ? '—'
                            : money
                            ? formatMoney(Number(v))
                            : ch.field === 'frequency'
                            ? PLEDGE_FREQUENCIES[v] || v
                            : v;
                        const label =
                          {
                            amount: 'Amount',
                            installments: 'Instalments',
                            frequency: 'Frequency',
                            total_value: 'Total pledged'
                          }[ch.field] || ch.field;
                        return (
                          <tr key={ch.id}>
                            <td className="text-nowrap">
                              {dayjs(ch.changed_at).format('DD/MM/YYYY HH:mm')}
                            </td>
                            <td>{label}</td>
                            <td>{fmt(ch.old_value)}</td>
                            <td className="fw-medium">{fmt(ch.new_value)}</td>
                            <td>
                              {ch.changed_by_user
                                ? `${ch.changed_by_user.first_name} ${ch.changed_by_user.last_name}`
                                : '—'}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </Table>
              )}
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
    </>
  );
};

export default PledgeProfile;
